/* eslint-disable @typescript-eslint/no-explicit-any */
// ToolCallsPopover uses dynamic message payloads that cannot be statically typed.

interface ToolCallsPopoverProps {
  toolCalls: any[];
  messages: any[];
  onClose: () => void;
}

export function ToolCallsPopover({
  toolCalls,
  messages,
  onClose,
}: ToolCallsPopoverProps) {
  return (
    <div className="bg-chariot-background-primary border-chariot-stroke tool-calls-container absolute top-full left-1/2 z-50 mt-2 max-h-60 w-96 -translate-x-1/2 overflow-y-auto rounded-lg border shadow-lg">
      <div className="space-y-1 p-2">
        {toolCalls.map((toolCall, index) => {
          const toolData = (() => {
            try {
              return JSON.parse(
                (toolCall as any).toolUseContent || toolCall.content
              );
            } catch {
              return null;
            }
          })();
          const toolName = toolData?.Name || toolData?.tool || 'tool';
          const input = toolData?.Input || toolData?.input || toolData?.params;

          const toolResponse = messages.find(
            m =>
              m.role === 'tool response' &&
              (m as any).toolUseId === (toolCall as any).toolUseId
          );
          const hasResponse = !!toolResponse;

          const responseSummary = (() => {
            if (!hasResponse || !toolResponse) return null;
            try {
              const d = JSON.parse(toolResponse.content);
              if (d.error)
                return { text: 'error', isError: true, errorMessage: d.error };
              if (d.count !== undefined)
                return { text: `${d.count} results`, isError: false };
              return { text: 'done', isError: false };
            } catch {
              return { text: 'done', isError: false };
            }
          })();

          const description = (() => {
            if (responseSummary?.isError && responseSummary.errorMessage)
              return responseSummary.errorMessage;
            if (toolName === 'query' && input?.node) {
              const labels = input.node.labels || [];
              if (labels.length > 0) {
                let desc = labels.join(', ');
                const filters = input.node.filters || [];
                if (filters.length > 0) {
                  desc += ` where ${filters.map((f: any) => `${f.field} ${f.operator} ${Array.isArray(f.value) ? `[${f.value.join(', ')}]` : f.value}`).join(', ')}`;
                }
                if (input.limit) desc += ` (limit ${input.limit})`;
                return desc;
              }
              return 'Query';
            }
            if (toolName === 'job' && input?.key)
              return `${input.capability || 'scan'} on ${input.key.split('#').pop() || 'target'}`;
            if (toolName === 'schema' && input?.entity_types)
              return Array.isArray(input.entity_types)
                ? input.entity_types.join(', ')
                : String(input.entity_types);
            return toolName.charAt(0).toUpperCase() + toolName.slice(1);
          })();

          return (
            <button
              key={toolCall.key || index}
              onClick={() => {
                const el = document.querySelector(
                  `[data-message-id="${toolCall.id || toolCall.key}"]`
                ) as HTMLElement;
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  const btn = el.querySelector('button');
                  if (btn) setTimeout(() => btn.click(), 300);
                }
                onClose();
              }}
              className="hover:bg-chariot-background-secondary w-full rounded px-3 py-2 text-left transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="flex w-5 flex-shrink-0 items-center justify-center">
                  {hasResponse ? (
                    responseSummary?.isError ? (
                      <span className="text-sm text-red-500">✕</span>
                    ) : (
                      <span className="text-success text-sm">✓</span>
                    )
                  ) : (
                    <div className="bg-chariot-text-quaternary size-2 animate-pulse rounded-full" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-chariot-text-primary truncate text-xs font-medium">
                    {description}
                  </div>
                </div>
                <div className="text-chariot-text-secondary flex-shrink-0 text-[10px]">
                  {responseSummary?.text || 'pending'}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
